import React, { useState, useEffect } from "react";
import { FormGroup, Label, Input, Button, Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import {
  useAddActionNoteMutation,
  useGetCommunitySettingsQuery,
  useUpdateCommunitySettingsMutation,
} from "../../../../store/api/userManagementApi.js";
import { useSelector } from "react-redux";
import AddCommentModal from "../shared/add_note.model.jsx";

// Map moderation settings to their respective types
const moderationTypes = {
  blockCommunityAccess: "GLOBAL_BLOCK_ACCESS",
  blockCommentAccess: "GLOBAL_BLOCK_ADD_COMMENT",
  blockAddingPosts: "GLOBAL_BLOCK_ADD_POST",
};

const UserModeration = () => {
  console.log("Rendering UserModeration");
  const userData = useSelector(state => state.userManagement.userData) || {};
  const user_id = userData?.user_id;
  const [moderationSettings, setModerationSettings] = useState({
    blockCommunityAccess: false,
    blockCommentAccess: false,
    blockAddingPosts: false,
  });
  const [initialSettings, setInitialSettings] = useState({});
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  const { data: settingsData, isError: isSettingsError } = useGetCommunitySettingsQuery(
    { user_id },
    { skip: !user_id }
  );
  const [updateCommunitySettings, { isLoading: isUpdatingSettings }] =
    useUpdateCommunitySettingsMutation();
  const [addActionNote] = useAddActionNoteMutation();

  useEffect(() => {
    // Guard against undefined (loading state). Using settingsData directly instead of
    // a defaulted `= []` prevents a new array reference being created each render,
    // which would otherwise trigger this effect on every render → infinite loop.
    if (!user_id || !Array.isArray(settingsData)) {
      return;
    }

    const fetchedSettings = {
      blockCommunityAccess: settingsData.some(
        (item) =>
          item.type === moderationTypes.blockCommunityAccess &&
          item.users?.some((user) => user.user_id === user_id)
      ),
      blockCommentAccess: settingsData.some(
        (item) =>
          item.type === moderationTypes.blockCommentAccess &&
          item.users?.some((user) => user.user_id === user_id)
      ),
      blockAddingPosts: settingsData.some(
        (item) =>
          item.type === moderationTypes.blockAddingPosts &&
          item.users?.some((user) => user.user_id === user_id)
      ),
    };

    setModerationSettings(fetchedSettings);
    setInitialSettings(fetchedSettings);
  }, [settingsData, user_id]);

  useEffect(() => {
    if (isSettingsError) {
      toast.error("Failed to fetch moderation settings.");
    }
  }, [isSettingsError]);

  const handleCheckboxChange = (field) => {
    setModerationSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    const payload = {
      data: {},
    };

    Object.keys(moderationSettings).forEach((key) => {
      if (moderationSettings[key] !== initialSettings[key]) {
        const type = moderationTypes[key];
        payload.data[type] = {
          new: moderationSettings[key] ? [user_id] : [],
          delete: !moderationSettings[key] ? [user_id] : [],
        };
      }
    });

    if (Object.keys(payload.data).length === 0) {
      toast.info("No changes to save.");
      return;
    }

    // Always show note modal when there are changes
    setPendingPayload(payload);
    setShowCommentModal(true);
  };

  // Function to execute the moderation update
  const handleModerationUpdate = async (payload) => {
    try {
      await updateCommunitySettings(payload).unwrap();
      toast.success("Moderation settings updated successfully.");
      setInitialSettings(moderationSettings);
    } catch (error) {
      toast.error("Failed to update moderation settings.");
    }
  };

  // Handle note submission for moderation actions
  const handleCommentSubmit = async (note) => {
    if (pendingPayload) {
      // First, try to add the note
      const commentData = {
        action_type: "user-moderation",
        note: note,
        user_id: user_id
      };

      // Try to add note first, but always execute moderation update
      try {
        const commentResponse = await addActionNote(commentData).unwrap();
        if (commentResponse.success) {
        } else {
          console.warn("Failed to add note:", commentResponse.message);
          toast.warning("Moderation update will proceed but note could not be saved");
        }
      } catch (error) {
        console.warn("Error adding note:", error);
        toast.warning("Moderation update will proceed but note could not be saved");
      }

      // Always execute the moderation update regardless of note result
      await handleModerationUpdate(pendingPayload);

      // Reset pending data
      setPendingPayload(null);
    }
  };

  return (
    <div >
      <div className="bg-transparent border-bottom mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="mb-0">User Moderation</h4>
          <p className="text-muted small mb-3">
            Manage user access and permissions
          </p>
        </div>
        <Button
          color="primary"
          disabled={isUpdatingSettings}
          onClick={handleSave}
          style={{ backgroundColor: '#8950fc', borderColor: '#8950fc' }}
          className="px-4"
        >
          {isUpdatingSettings ? 'Updating...' : 'Update'}
        </Button>
      </div>
      <div className="mt-4">
        <Row>
          <Col md={12}>
            <FormGroup check className="border-200">
              <Label check className="fs--1 text-900">
                <Input
                  type="checkbox"
                  checked={moderationSettings.blockCommunityAccess}
                  onChange={() => handleCheckboxChange("blockCommunityAccess")}
                  disabled={isUpdatingSettings}
                />
                Block Community Access
              </Label>
            </FormGroup>
            <FormGroup check className="border-200">
              <Label check className="fs--1 text-900">
                <Input
                  type="checkbox"
                  checked={moderationSettings.blockCommentAccess}
                  onChange={() => handleCheckboxChange("blockCommentAccess")}
                  disabled={isUpdatingSettings}
                />
                Block Adding Comments
              </Label>
            </FormGroup>
            <FormGroup check className="border-200">
              <Label check className="fs--1 text-900">
                <Input
                  type="checkbox"
                  checked={moderationSettings.blockAddingPosts}
                  onChange={() => handleCheckboxChange("blockAddingPosts")}
                  disabled={isUpdatingSettings}
                />
                Block Adding Posts
              </Label>
            </FormGroup>
          </Col>
        </Row>
      </div>

      {/* Comment Modal */}
      <AddCommentModal
        isOpen={showCommentModal}
        toggle={() => {
          setShowCommentModal(false);
          setPendingPayload(null);
        }}
        onSubmit={handleCommentSubmit}
        actionType="user-moderation"
      />
    </div>
  );
};

export default UserModeration;